import { useTitle } from '@e2m-customer-portal/hooks';
import { MaintenanceBanner } from '@e2m-customer-portal/ui';
import { Hidden, Toolbar, Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';

import React, { MouseEventHandler, ReactElement } from 'react';
import { makeStyles } from 'tss-react/mui-compat';
import { MenuIcon } from '../../assets/icons';
import { HeaderLogo } from './HeaderLogo';
import { HeaderMenu } from './HeaderMenu';

const useStyles = makeStyles()((theme) => ({
  appBar: {
    backgroundColor: 'white',
    top: 0,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: theme.palette.grey[100],
    position: 'sticky',
    color: 'inherit',
    zIndex: theme.zIndex.drawer + 1,
    paddingRight: `0 !important`,
  },

  sectionMobile: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
    zIndex: theme.zIndex.drawer,
  },
}));

interface HeaderProps {
  /**
   * Event handler that is called, if the menu icon is clicked on small screens.
   * This event handler should then toggle visibility of the sidebar.
   */
  onMenuClick?: MouseEventHandler<HTMLButtonElement>;

  /**
   * Sets the header variant. `normal` is the default. `public` will hide the
   * user settings area and sidebar button.
   */
  variant?: 'normal' | 'public';

  /**
   * The extra content of the header.
   */
  children?: React.ReactNode;

  /**
   * Meta prop to coordinate styling across sibling components
   */
  sidebarIsExpanded: boolean;

  /**
   * Meta prop to coordinate styling across sibling components
   */
  onMaintenanceBannerHeightChange?: React.Dispatch<React.SetStateAction<number>>;
}

export function Header({
  onMenuClick,
  variant = 'normal',
  children,
  sidebarIsExpanded,
  onMaintenanceBannerHeightChange,
}: Readonly<HeaderProps>): ReactElement<HeaderProps> {
  const { classes } = useStyles();
  const { pageTitle } = useTitle();

  return (
    <AppBar className={classes.appBar} elevation={0}>
      {onMaintenanceBannerHeightChange && <MaintenanceBanner onHeightChange={onMaintenanceBannerHeightChange} />}
      <Toolbar disableGutters className="items-stretch">
        {variant === 'normal' && (
          <div className={classes.sectionMobile}>
            <IconButton aria-label="show more" aria-haspopup="true" color="inherit" size="small" onClick={onMenuClick}>
              <MenuIcon className="w-5" />
            </IconButton>
          </div>
        )}
        <HeaderLogo sidebarIsExpanded={sidebarIsExpanded} />
        <Hidden mdDown>
          <HeaderMenu />
        </Hidden>
        <Hidden mdUp>
          <Typography variant="h2" className="self-center text-base font-medium">
            {pageTitle}
          </Typography>
        </Hidden>
        <div className="grow" />
        {children}
      </Toolbar>
    </AppBar>
  );
}
